/**
 * Warrior Recharge — lightweight landing quiz.
 * Progressive enhancement; no dependencies.
 */
class WrWarriorTest extends HTMLElement {
  connectedCallback() {
    this.prompt = this.querySelector('[data-wr-quiz-prompt]');
    this.result = this.querySelector('[data-wr-quiz-result]');
    this.questionEl = this.querySelector('[data-wr-quiz-question]');
    this.progress = this.querySelector('[data-wr-quiz-progress]');
    this.restartBtn = this.querySelector('[data-wr-quiz-restart]');
    this.choiceButtons = Array.from(this.querySelectorAll('[data-wr-quiz-choice]'));

    this.questions = Array.from(this.querySelectorAll('[data-wr-quiz-dot]')).map(
      (dot) => dot.getAttribute('data-question') || ''
    );

    this.index = 0;
    this.boundOnChoice = this.onChoice.bind(this);
    this.boundOnRestart = this.onRestart.bind(this);

    this.choiceButtons.forEach((btn) => btn.addEventListener('click', this.boundOnChoice));
    this.restartBtn?.addEventListener('click', this.boundOnRestart);

    this.render();
  }

  disconnectedCallback() {
    this.choiceButtons.forEach((btn) => btn.removeEventListener('click', this.boundOnChoice));
    this.restartBtn?.removeEventListener('click', this.boundOnRestart);
  }

  onChoice() {
    if (this.index >= this.questions.length - 1) {
      this.showResult();
      return;
    }
    this.index += 1;
    this.render();
  }

  onRestart() {
    this.index = 0;
    this.result?.classList.remove('is-active');
    this.prompt?.classList.remove('is-hidden');
    this.render();
    this.questionEl?.focus();
  }

  showResult() {
    this.prompt?.classList.add('is-hidden');
    this.result?.classList.add('is-active');
    this.updateProgress(true);
    this.result?.querySelector('[data-wr-quiz-result-title]')?.focus();
  }

  render() {
    if (!this.questions.length || !this.questionEl) return;

    this.questionEl.textContent = this.questions[this.index] || '';
    this.updateProgress(false);
  }

  updateProgress(complete) {
    if (!this.progress) return;

    const dots = Array.from(this.progress.querySelectorAll('[data-wr-quiz-dot]'));
    dots.forEach((dot, i) => {
      const isCurrent = !complete && i === this.index;
      const isComplete = complete || i < this.index;
      if (isCurrent) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
      dot.classList.toggle('is-complete', isComplete);
    });
  }
}

if (!customElements.get('wr-warrior-test')) {
  customElements.define('wr-warrior-test', WrWarriorTest);
}

/**
 * Subtle enter animation for landing sections.
 */
function initWrLandingReveals() {
  const nodes = document.querySelectorAll('.wr-landing__reveal:not([data-wr-reveal-bound])');
  if (!nodes.length) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion || !('IntersectionObserver' in window)) {
    nodes.forEach((el) => {
      el.classList.add('is-visible');
      el.setAttribute('data-wr-reveal-bound', '');
    });
    return;
  }

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        obs.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.15 }
  );

  nodes.forEach((el) => {
    el.setAttribute('data-wr-reveal-bound', '');
    observer.observe(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWrLandingReveals, { once: true });
} else {
  initWrLandingReveals();
}

document.addEventListener('shopify:section:load', initWrLandingReveals);
